/**
 * Nether mobs (vanilla Java values, normal difficulty):
 *  - zombified piglin: 20 HP, neutral; hitting one angers it and every
 *    zombified piglin within 20 blocks for 20-39 s; golden sword 8 damage
 *    (unarmed 5); speed 0.23. Fire/lava immune.
 *  - piglin: 16 HP, golden sword 5 damage; hostile unless the player wears gold
 *    armour; gold ingot barter (barter.ts); speed 0.35.
 *  - magma cube: size 4/2/1 = 16/4/1 HP, 6/4/3 damage, hops toward the
 *    player, splits into 2-4 smaller cubes on death. Fire/lava immune.
 *
 * Ghast, blaze, wither skeleton, hoglin and strider are TODO.
 */

import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import { Mob, type MobCtx } from '../mob';
import { Blaze, Enderman, Ghast, Hoglin, Strider, WitherSkeleton } from './mobs2';
import { isSpearId, mobSpearDamage, rollMobSpear } from '../../game/spear';
import { ADMIRE_TICKS, angerPiglinsNear, piglinHooks, piglinRegistry, rollBarter, type PiglinLike, type PiglinTarget } from './barter';

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

/** Hook main.ts installs so a dying magma cube can split (spawn + resize). */
export const netherHooks: {
  spawn: ((type: MobType, x: number, y: number, z: number) => Mob | null) | null;
  /** Upward toss of the player (hoglin hits). */
  toss?: (vy: number) => void;
  /** Visual effects (enderman teleport puff). */
  effect?: (kind: 'portal', x: number, y: number, z: number) => void;
  /** World edits for mobs (enderman block carrying). */
  setBlock?: (x: number, y: number, z: number, id: number) => boolean;
} = { spawn: null };

/** Shared chase/target logic (like hostile.ts's, which is module-private). */
abstract class NetherHostile extends Mob {
  protected seeMemory = 0;
  protected angry = true;
  protected attackCd = 0;

  /** Fire and lava immune (vanilla fire_immune): no lava/fire damage, never burns.
   *  Public so the magma-block check in main.ts can read it. */
  fireImmune = false;
  private ticking = false;

  override tick(ctx: MobCtx): void {
    if (this.fireImmune) this.fireTicks = 0;
    this.ticking = true;
    try { super.tick(ctx); } finally { this.ticking = false; }
    if (this.fireImmune) this.fireTicks = 0;
  }

  override hurt(amount: number): boolean {
    // inside our own tick the only damage sources are lava, fire and falls
    if (this.fireImmune && this.ticking && (this.inLava || this.fireTicks > 0)) { this.fireTicks = 0; return false; }
    return super.hurt(amount);
  }

  xpValue(): number { return 5; }

  protected target(ctx: MobCtx, range: number): boolean {
    const p = ctx.host.player;
    if (!ctx.targetable() || !this.angry) { this.seeMemory = 0; return false; }
    const dx = p.x - this.x, dy = p.y - this.y, dz = p.z - this.z;
    if (dx * dx + dy * dy + dz * dz > range * range) { this.seeMemory = 0; return false; }
    if (this.phase(ctx, 5) && ctx.los(this.x, this.eyeY, this.z, p.x, p.eyeY, p.z)) this.seeMemory = 60;
    if (this.seeMemory > 0) this.seeMemory--;
    if (this.retaliate > 0) this.seeMemory = Math.max(this.seeMemory, 1);
    return this.seeMemory > 0;
  }

  protected melee(ctx: MobCtx, damage: number, mod: number): void {
    const p = ctx.host.player;
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    this.steerTo(ctx, p.x, p.y, p.z, mod, 6);
    if (this.attackCd === 0 && this.inMeleeReach(ctx)) {
      this.attackCd = 20;
      this.swing();
      ctx.hurtPlayer(this, isSpearId(this.heldItem) ? mobSpearDamage(this.heldItem, Math.hypot(this.vx, this.vz)) : damage, 0.4);
    }
  }
}

const angryPiglins = new Set<ZombifiedPiglin>();

export class ZombifiedPiglin extends NetherHostile {
  private anger = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'zombified_piglin', x, y, z, { width: 0.6, height: 1.95, eye: 1.79, health: 20, speed: 0.23 }, rnd);
    this.heldItem = rollMobSpear('zombified_piglin', rnd) || I.GOLDEN_SWORD; // golden spear (game/spear.ts)
    this.fireImmune = true;
    this.angry = false;
    angryPiglins.add(this);
  }

  override onHurtBy(_fx: number, _fz: number, rnd: () => number): void {
    // vanilla: the whole group within 20 blocks turns on the player for 20-39 s
    for (const z of angryPiglins) {
      if (z.removed) { angryPiglins.delete(z); continue; }
      if (z.realm !== this.realm) continue;
      const dx = z.x - this.x, dz = z.z - this.z, dy = z.y - this.y;
      if (dx * dx + dy * dy + dz * dz <= 400) z.provoke(400 + Math.floor(rnd() * 380));
    }
  }

  provoke(ticks: number): void {
    this.anger = Math.max(this.anger, ticks);
    this.retaliate = Math.max(this.retaliate, 60);
  }

  protected ai(ctx: MobCtx): void {
    if (this.removed || this.dying) angryPiglins.delete(this);
    if (this.attackCd > 0) this.attackCd--;
    if (this.anger > 0) this.anger--;
    this.angry = this.anger > 0;
    if (this.target(ctx, 35)) {
      this.melee(ctx, 8, 1.2); // 0.23 -> ~0.28 angry
    } else {
      this.stroll(ctx, 0.8, 4, false);
      this.lookAtNearPlayer(ctx);
    }
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    const out: Stack[] = [];
    const flesh = roll(rnd, 0, 1);
    if (flesh) out.push(stackOf(I.ROTTEN_FLESH, flesh));
    const nug = roll(rnd, 0, 1);
    if (nug) out.push(stackOf(I.GOLD_NUGGET, nug));
    if (byPlayer && rnd() < 0.025) out.push(stackOf(I.GOLD_INGOT, 1));
    if (byPlayer && rnd() < 0.085) out.push(stackOf(I.GOLDEN_SWORD, 1));
    return out;
  }
}

export class Piglin extends NetherHostile implements PiglinLike {
  /** Ticks of grudge left (hit, chest opened, gold mined nearby). */
  private anger = 0;
  /** Ticks left admiring a gold ingot (0 = not bartering). Mirrored to view.color for the renderer. */
  admire = 0;
  /** Crossbow variant (ranged) instead of the golden sword. */
  crossbow = false;
  private charge = 0;
  /** Golden spear in place of the sword (game/spear.ts), 0 = none. */
  private spear = 0;
  private weapon(): number { return this.crossbow ? I.CROSSBOW : (this.spear || I.GOLDEN_SWORD); }
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'piglin', x, y, z, { width: 0.6, height: 1.95, eye: 1.79, health: 16, speed: 0.35 }, rnd);
    this.crossbow = rnd() < 0.5; // vanilla: half the adults carry crossbows
    if (!this.crossbow) this.spear = rollMobSpear('piglin', rnd);
    this.heldItem = this.weapon();
    piglinRegistry.add(this);
  }

  /** Is this piglin hostile to the player right now? Gold armour makes them neutral unless provoked. */
  hostileNow(): boolean {
    // host.player is the player this piglin would go for (the nearest one in multiplayer)
    return this.anger > 0 || this.retaliate > 0 || !piglinHooks.wearsGold(this.host?.player as PiglinTarget | undefined);
  }

  provoke(ticks: number): void {
    this.anger = Math.max(this.anger, ticks);
    this.retaliate = Math.max(this.retaliate, 60);
    this.cancelAdmire();
  }

  override onHurtBy(): void {
    // vanilla PiglinAi.wasHurtBy: the group within 16 blocks joins in
    this.anger = Math.max(this.anger, 600);
    this.retaliate = 200;
    this.cancelAdmire();
    angerPiglinsNear(this.x, this.y, this.z);
  }

  private cancelAdmire(): void {
    if (this.admire <= 0) return;
    this.admire = 0; this.color = 0; this.heldItem = this.weapon();
  }

  /**
   * Player offers `itemId` (right-click). A gold ingot starts the 6 s admire
   * if the piglin is calm and idle. Returns whether the item was taken.
   */
  tryBarter(itemId: number): boolean {
    if (itemId !== I.GOLD_INGOT || !this.alive || this.admire > 0) return false;
    if (this.anger > 0 || this.retaliate > 0) return false;
    this.admire = ADMIRE_TICKS;
    this.color = 1;
    this.heldItem = I.GOLD_INGOT;
    return true;
  }

  saveExtra(): unknown { return [this.anger, this.admire, this.crossbow ? 1 : 0]; }
  loadExtra(d: unknown): void {
    const a = d as number[];
    this.anger = typeof a?.[0] === 'number' ? a[0] : 0;
    this.admire = typeof a?.[1] === 'number' ? a[1] : 0;
    this.crossbow = a?.[2] === 1;
    this.color = this.admire > 0 ? 1 : 0;
    this.heldItem = this.admire > 0 ? I.GOLD_INGOT : this.weapon();
  }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.anger > 0) this.anger--;
    if (this.admire > 0) {
      const p = ctx.host.player;
      this.faceToward(p.x, p.z);
      this.lookAt(p.x, p.eyeY, p.z);
      if (--this.admire === 0) this.finishBarter(ctx);
      return;
    }
    this.angry = this.hostileNow();
    if (this.target(ctx, 16)) {
      if (this.crossbow) this.shootCrossbow(ctx); else this.melee(ctx, 5, 1);
    } else if (this.anger <= 0 && this.retaliate <= 0 && this.scavengeGold(ctx)) { /* walking to a dropped ingot */ }
    else {
      this.stroll(ctx, 0.6, 4, false);
      this.lookAtNearPlayer(ctx);
    }
  }

  /** Vanilla RangedCrossbowAttackGoal: keep 5..10 blocks away, 25-tick charge, arrow power 1.6. */
  private shootCrossbow(ctx: MobCtx): void {
    const p = ctx.host.player;
    const dx = p.x - this.x, dz = p.z - this.z;
    const d = Math.hypot(dx, dz) || 1;
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    if (d > 10) this.steerTo(ctx, p.x, p.y, p.z, 1, 6);
    else if (d < 5) this.navAway(ctx, p.x, p.z, 0.8, 3, false);
    if (d < 16 && ctx.los(this.x, this.eyeY, this.z, p.x, p.eyeY, p.z)) {
      if (this.charge < 25) this.charge++;
      if (this.charge >= 25 && this.attackCd === 0) {
        const sy = this.eyeY - 0.1;
        ctx.shootArrow(this.x, sy, this.z, dx, p.y + p.height * 0.333 - sy + d * 0.2, dz, 1.6, 2, false, 6, this.id);
        this.charge = 0;
        this.attackCd = 20 + Math.floor(ctx.host.rnd() * 20);
        this.swing();
      }
    } else if (this.charge > 0) this.charge--;
  }

  /** Walk to a dropped gold ingot within 8 blocks and pick it up to barter (vanilla PiglinAi pickup). */
  private scavengeGold(ctx: MobCtx): boolean {
    const g = piglinHooks.findGold?.(this.x, this.y, this.z, 8);
    if (!g) return false;
    if ((g.x - this.x) ** 2 + (g.z - this.z) ** 2 < 1.2 * 1.2 && Math.abs(g.y - this.y) < 1.5) {
      g.take();
      this.tryBarter(I.GOLD_INGOT);
      return true;
    }
    this.faceToward(g.x, g.z);
    this.steerTo(ctx, g.x, g.y, g.z, 1, 4);
    return true;
  }

  /** Throw the barter result toward the player (vanilla PiglinAi.throwItems). */
  private finishBarter(ctx: MobCtx): void {
    this.color = 0;
    this.heldItem = this.weapon();
    const p = ctx.host.player;
    const dx = p.x - this.x, dz = p.z - this.z;
    const l = Math.hypot(dx, dz) || 1;
    ctx.host.dropItem(rollBarter(ctx.host.rnd), this.x, this.y + 1.3, this.z, (dx / l) * 0.25, 0.1, (dz / l) * 0.25);
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    if (!byPlayer || rnd() >= 0.085) return [];
    return this.crossbow ? [{ id: I.CROSSBOW, count: 1, dmg: Math.floor(rnd() * 400) }] : [stackOf(I.GOLDEN_SWORD, 1)];
  }
}

const CUBE_HP = [0, 1, 4, 0, 16];
const CUBE_DMG = [0, 3, 4, 0, 6];

export class MagmaCube extends NetherHostile {
  /** 1, 2 or 4 (vanilla size 1/2/4, hitbox 0.52 * size). Mirrored to view.color for the renderer. */
  size = 4;
  private jumpDelay = 0;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'magma_cube', x, y, z, { width: 2.04, height: 2.04, eye: 1.3, health: 16, speed: 0.2 }, rnd);
    this.fireImmune = true;
    this.noFallDamage = true;
    this.jumpDelay = 10 + Math.floor(rnd() * 20);
    this.setSize(rnd() < 0.33 ? 1 : rnd() < 0.5 ? 2 : 4);
  }

  setSize(s: number): void {
    this.size = s;
    const w = 0.52 * s;
    const self = this as unknown as { width: number; height: number; eyeHeight: number; maxHealth: number };
    self.width = w; self.height = w; self.eyeHeight = w * 0.625;
    self.maxHealth = CUBE_HP[s] ?? 16;
    this.health = self.maxHealth;
    this.color = s;
  }

  protected override floats(): boolean { return true; }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.dying) return;
    const p = ctx.host.player;
    const hunting = this.target(ctx, 16);
    if (hunting) {
      this.aggressive = true;
      this.faceToward(p.x, p.z);
      if (this.attackCd === 0 && this.inMeleeReach(ctx)) {
        this.attackCd = 20;
        ctx.hurtPlayer(this, CUBE_DMG[this.size] ?? 3, 0.4);
      }
    }
    // hop: only moves while airborne, waits on the ground between jumps
    if (this.onGround) {
      if (this.jumpDelay > 0) { this.jumpDelay--; return; }
      this.jumpDelay = Math.floor(ctx.host.rnd() * 20) + (hunting ? 3 : 10);
      this.vy = 0.42 + 0.1 * this.size; // magma cubes jump higher than slimes
      const a = hunting ? Math.atan2(p.z - this.z, p.x - this.x) : ctx.host.rnd() * Math.PI * 2;
      const sp = 0.1 + 0.05 * this.size;
      this.vx = Math.cos(a) * sp;
      this.vz = Math.sin(a) * sp;
      this.swell = 1; // squash anim
    } else if (this.swell > 0) this.swell = Math.max(0, this.swell - 0.1);
  }

  override hurt(amount: number): boolean {
    const was = this.dying;
    const r = super.hurt(amount);
    if (!was && this.dying && this.size > 1 && netherHooks.spawn) {
      const n = 2 + Math.floor(this.rng() * 3);
      for (let i = 0; i < n; i++) {
        const ox = ((i % 2) - 0.5) * this.size * 0.25, oz = ((i >> 1) - 0.5) * this.size * 0.25;
        const c = netherHooks.spawn('magma_cube', this.x + ox, this.y + 0.5, this.z + oz);
        if (c instanceof MagmaCube) c.setSize(this.size / 2);
      }
    }
    return r;
  }

  xpValue(): number { return this.size; }

  loot(rnd: () => number): Stack[] {
    // vanilla: big/medium cubes drop 0-1 magma cream (25%)
    return this.size > 1 && rnd() < 0.25 ? [stackOf(I.MAGMA_CREAM, 1)] : [];
  }
}

export const NETHER_MOB_TYPES: readonly MobType[] = [
  'zombified_piglin', 'piglin', 'magma_cube', 'ghast', 'blaze', 'wither_skeleton', 'hoglin', 'strider', 'enderman',
];

export function createNetherMob(type: MobType, id: number, x: number, y: number, z: number, rnd: () => number): Mob | null {
  switch (type) {
    case 'zombified_piglin': return new ZombifiedPiglin(id, x, y, z, rnd);
    case 'piglin': return new Piglin(id, x, y, z, rnd);
    case 'magma_cube': return new MagmaCube(id, x, y, z, rnd);
    case 'ghast': return new Ghast(id, x, y, z, rnd);
    case 'blaze': return new Blaze(id, x, y, z, rnd);
    case 'wither_skeleton': return new WitherSkeleton(id, x, y, z, rnd);
    case 'hoglin': return new Hoglin(id, x, y, z, rnd);
    case 'strider': return new Strider(id, x, y, z, rnd);
    case 'enderman': return new Enderman(id, x, y, z, rnd);
    default: return null;
  }
}
